import { TFunction } from "i18next";
import { z } from "zod";
import { NotificationCategory } from "../types/userSettings.definition";

function isAbsoluteUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// ── Admin profile (MED-1448) ──────────────────────────────────────────────

/**
 * The photo is the only account field the BFF lets its owner change on its
 * own, through `PUT /users/{id}/profile-image`. `PUT /users/{id}` takes the
 * create-shaped `UserRequest`, whose mandatory `password` the API discards,
 * so it is unusable for a self-service edit — the rest of the profile is
 * therefore shown read-only rather than as controls that cannot save.
 */
export const adminProfileSchema = (t: TFunction) =>
  z.object({
    photoUrl: z
      .string()
      .min(1, t("validation.required"))
      .refine(isAbsoluteUrl, { message: t("validation.urlInvalid") }),
  });

export type AdminProfileFormData = z.infer<
  ReturnType<typeof adminProfileSchema>
>;

// ── Admin settings (MED-1449) ─────────────────────────────────────────────

/** The two languages the admin ships catalogues for, as `LanguageEnum` names. */
export const ADMIN_SETTINGS_LANGUAGES = ["FR", "EN"] as const;

export const adminSettingsSchema = (t: TFunction) =>
  z.object({
    appLanguage: z.enum(ADMIN_SETTINGS_LANGUAGES, {
      error: () => ({ message: t("validation.required") }),
    }),
    notifications: z.array(z.enum(NotificationCategory)),
  });

export type AdminSettingsFormData = z.infer<
  ReturnType<typeof adminSettingsSchema>
>;
